import type { IAddon, IData, IDataPoint, IEvent, IItem } from 'Components/sq-graphics/interfaces';
import { exists, formatTime, isNumber, isPartialDate, isTime, partialDateToValue, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { convertTrendToIcon } from 'Routes/Imaging/utils';
import { getICD10Description } from 'Utility/randomUtil';
import { equals } from 'ramda';
import { examination } from 'neuro-schemas';
import { DateTime } from 'luxon';
import { getBackgroundTimeseries, getBMITimeseries, getMeasurementTimeseries } from 'neuro-calculation-commons';
import { filterDocumentsByType, mapDocumentToNeuroDocument } from 'Utility/documentHandling';

const {
  config: { abnormalAbnormalFlags, abnormalFlagsWithDescription },
} = examination;

export const convertDiagnosisToTimeline = (
  docs: Array<IDocumentWithTypeV2<'diagnosis'>>,
  platform: Platform | null,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (name: string) => string,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (platform === 'ms') {
      const msTypeDocs = d.typeOfDisease ?? [];
      if (msTypeDocs.some(msTypeDoc => equals(d.date, d.date))) return;
    }
    const dxCode = d.diagnosis ?? '';
    const title: string = [
      'unspecified',
      'sleepApneaSuspicion',
      'respiratoryFailureSuspicion',
      'radiologicallyIsolatedSyndrome',
      'nonInvasiveNeuroModulationTherapy',
      'alzheimerSuspicion',
    ].includes(dxCode)
      ? fm(`diagnosis.${dxCode === 'radiologicallyIsolatedSyndrome' ? 'msTitle' : 'opts'}.${dxCode}`)
      : ['unspecifiedDiagnosis'].includes(dxCode)
        ? fm('diagnosis.opts.unspecifiedDiagnosis')
        : dxCode;
    const events: IEvent[] = [];

    // For MGravis G70.0, generate diagnosis entries from formOfDisease items unless no forms exist
    if (dxCode === 'G70.0' && Array.isArray(d.formOfDisease) && d.formOfDisease.some(form => form.formOfDisease)) {
      // If diagnosis date is not on the same date as any of the forms of disease, add dg separately
      if (!d.formOfDisease.some(fod => equals(fod.date, d.date))) {
        events.push({
          date: dateFromPartialUpdateTimeframe(d.date, undefined, true),
          eventType: 'text',
          title,
        });
      }
      // Add diagnosis event for each of the forms of disease
      d.formOfDisease.forEach(form => {
        form.date &&
          form.formOfDisease &&
          events.push({
            date: dateFromPartialUpdateTimeframe(form.date, undefined, true),
            eventType: 'text',
            title:
              dxCode +
              ` (${
                form.formOfDisease === 'generalized'
                  ? fm('diagnosis.mgravis.formOfDisease.generalizedShort')
                  : form.formOfDisease === 'ocular'
                    ? fm('diagnosis.mgravis.formOfDisease.ocularShort')
                    : ''
              })`,
          });
      });
    } else {
      events.push({
        date: dateFromPartialUpdateTimeframe(d.date, undefined, true),
        eventType: 'text',
        title,
        description: d.diagnosis === 'unspecifiedDiagnosis' ? d.unspecifiedDetails : undefined,
      });
    }
    addons.push({
      id: 'diagnosis',
      title: fm('diagnosis.title').toLocaleUpperCase(),
      titleDescription: undefined,
      items: [],
      events,
    });
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertAppointmentToTimeline = (
  docs: Array<IAppointment>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): IAddon[] | undefined => {
  const addons: Array<IAddon> = [];
  const events: IEvent[] = [];
  docs.forEach((d: IAppointment) => {
    if (!isPartialDate(d.startDate)) return;
    const description = [
      {
        title: fm('appointments.startTime'),
        values: formatTime(d.startTime),
        condition: isTime(d.startTime),
      },
      {
        title: fm('appointments.status'),
        values: fm(`appointments.opts.${d.status}`),
        condition: exists(d.status),
      },
      {
        title: fm('appointments.externalAppointmentType'),
        values: d.externalAppointmentType ?? '-',
        condition: exists(d.externalAppointmentType),
      },
      {
        title: fm('appointments.locationGroup.title'),
        values: (
          <>
            <div>{`${fm('appointments.locationGroup.id')}: ${d.locationGroupId}`}</div>
            <div>{`${fm('appointments.locationGroup.name')}: ${d.locationGroupName}`}</div>
          </>
        ),
        condition: exists(d.locationGroupId ?? d.locationGroupName),
      },
      {
        title: fm('appointments.locationType.title'),
        values: (
          <>
            <div>{`${fm('appointments.locationType.id')}: ${d.locationTypeId}`}</div>
            <div>{`${fm('appointments.locationType.text')}: ${d.locationTypeText}`}</div>
          </>
        ),
        condition: exists(d.locationTypeId ?? d.locationTypeText),
      },
      {
        title: fm('appointments.location.title'),
        values: (
          <>
            <div>{`${fm('appointments.location.id')}: ${d.locationId}`}</div>
            <div>{`${fm('appointments.location.name')}: ${d.locationName}`}</div>
          </>
        ),
        condition: exists(d.locationId ?? d.locationName),
      },
      {
        title: fm('appointments.ended'),
        values: `${formatPartialDate(d.endDate ?? undefined)}${isTime(d.endTime) ? ` (${formatTime(d.endTime)})` : ''}`,
        condition: isPartialDate(d.endDate),
      },
      {
        title: fm('appointments.comments'),
        values: d.comments ?? '-',
        condition: exists(d.comments),
      },
    ];
    events.push({
      date: dateFromPartialUpdateTimeframe(d.startDate),
      title: fm('appointments.appointment'),
      eventType: 'appointment',
      description,
    });
  });
  addons.push({
    id: 'appointments',
    title: fm('appointments.appointments'),
    titleDescription: undefined,
    items: [],
    events,
  });
  return events.length > 0 ? addons : undefined;
};

export const convertTherapyToTimeline = (
  docs: IDocumentWithTypeV2<'therapy'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): IAddon[] | undefined => {
  const addons: Array<IAddon> = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    switch (d.therapyTypes) {
      case 'physiotherapySessions': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.physiotherapySessions'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.physiotherapySessions'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'respiratoryPhysiotherapySessions': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.respiratoryPhysiotherapySessions'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.respiratoryPhysiotherapySessions'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'ridingTherapy': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.ridingTherapy'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.ridingTherapy'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'homeProgramme': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.homeProgramme'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.homeProgramme'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'hydroTherapyWaterBasedTherapy': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.hydroTherapyWaterBasedTherapy'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.hydroTherapyWaterBasedTherapy'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'ManagementOfContracturesUsingOrthotics': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.ManagementOfContracturesUsingOrthotics'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.ManagementOfContracturesUsingOrthotics'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'brace': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.brace'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.brace'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'OccupationalTherapySessions': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.OccupationalTherapySessions'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.OccupationalTherapySessions'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'SpeechLanguageTherapySessions': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('therapy.opts.SpeechLanguageTherapySessions'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.SpeechLanguageTherapySessions'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
      case 'others': {
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: d.otherTherapyType ? `${fm('therapy.opts.others')}: ${d.otherTherapyType}` : fm('therapy.opts.others'),
        });
        addons.push({
          id: d.therapyTypes,
          title: fm('therapy.opts.others'),
          titleDescription: undefined,
          items,
          events: [],
        });
        break;
      }
    }
  });
  return addons.length > 0 ? addons : undefined;
};

const docIsImagingIntegration = (
  d: IImagingIntegrationFields & IControlProps,
): d is IImagingIntegrationFields & IControlProps => {
  if (d.procedureCode || d.statement) return true;
  return false;
};

export const convertImagingToTimeline = (
  docs: Array<
    IDocumentWithTypeV2<
      | 'dexa'
      | 'cmap'
      | 'muscleImaging'
      | 'scoliosis'
      | 'pelvis'
      | 'mri'
      | 'scintigraphy'
      | 'tt'
      | 'echocardiography'
      | 'thymusCt'
      | 'thymusMri'
    >
  >,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string, values?: { [key: string]: string }) => string,
  platform: Platform | null,
  ariaSymptomDocs?: Array<IDocumentWithTypeV2<'ariaSymptoms'>>,
  ariaInterestedMedications?: Array<IDocumentWithTypeV2<'medication'>>,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];

  const allMriDocs = filterDocumentsByType(docs, ['mri']).toSorted((m1, m2) => sortPartialDate(m1.date, m2.date));
  const preMedMris: Array<(IDocumentWithTypeV2<'mri'> & { medDocId?: string }) | undefined> | undefined =
    ariaInterestedMedications
      ?.map(aim => {
        const medDateTime = DateTime.fromMillis(partialDateToValue(aim.startDate));
        const oldestAllowedMriPreMed = medDateTime.minus({ months: 6 });
        const allMrisPreThisMed = allMriDocs.filter(m => {
          const mriDateTime = DateTime.fromMillis(partialDateToValue(m.date));
          return (
            partialDateToValue(m.date) < partialDateToValue(aim.startDate) &&
            mriDateTime.diff(oldestAllowedMriPreMed).toMillis() > 0
          );
        });
        if (allMrisPreThisMed.length === 0) return;
        return { ...allMrisPreThisMed[allMrisPreThisMed.length - 1], medDocId: aim._id };
      })
      .filter(prune => prune);

  docs.forEach((d, index, array) => {
    const showProcedureCode = docIsImagingIntegration(d) && (d.procedureCode ?? '').length > 0;

    const showStatement = docIsImagingIntegration(d) && (d.statement ?? '').length > 0;

    switch (d._type) {
      case 'dexa': {
        if (platform && ['sma', 'dmd'].includes(platform)) {
          const events: IEvent[] = [];
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.dexa'),
              description: [
                {
                  title: fm('imaging.procedureCode'),
                  values: d.procedureCode ?? undefined,
                  condition: !!showProcedureCode,
                },
                {
                  title: fm('imaging.statementText'),
                  values: d.statement ?? undefined,
                  condition: !!showStatement,
                },
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'cmap': {
        if (platform && ['sma', 'dmd'].includes(platform)) {
          const events: IEvent[] = [];
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.cmap'),
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'muscleImaging': {
        if (platform && ['sma', 'dmd'].includes(platform)) {
          const events: IEvent[] = [];
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.muscleImaging'),
              description: [
                {
                  title: fm('imaging.procedureCode'),
                  values: d.procedureCode ?? undefined,
                  condition: !!showProcedureCode,
                },
                {
                  title: fm('imaging.statementText'),
                  values: d.statement ?? undefined,
                  condition: !!showStatement,
                },
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'echocardiography': {
        if (platform === 'dmd') {
          if (!isPartialDate(d.date)) return undefined;

          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('imaging.echocardiography'),
            description: [
              {
                title: fm('imaging.lvef'),
                values: d.lvef ?? undefined,
              },
              {
                title: fm('imaging.fs'),
                values: d.fs ?? undefined,
              },
            ],
          });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'scoliosis': {
        if (platform && ['sma', 'dmd'].includes(platform)) {
          const events: IEvent[] = [];
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.scoliosis'),
              description: [
                {
                  title: fm('imaging.procedureCode'),
                  values: d.procedureCode ?? undefined,
                  condition: !!showProcedureCode,
                },
                {
                  title: fm('imaging.statementText'),
                  values: d.statement ?? undefined,
                  condition: !!showStatement,
                },
                ...[
                  'proximalThoracicCobb',
                  'thoracicCobb',
                  'thoracolumbarCobb',
                  'kyphosist2t12',
                  'kyphosist5t12',
                  'lordosist12s1',
                ].map(name => {
                  return {
                    title: fm(`imaging.${name}`),
                    values: exists(d[name as keyof IScoliosisRontgen])
                      ? `${d[name as keyof IScoliosisRontgen]}°`
                      : undefined,
                  };
                }),
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'pelvis': {
        if (platform && ['sma', 'dmd'].includes(platform)) {
          const events: IEvent[] = [];
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.pelvis'),
              description: [
                {
                  title: fm('imaging.procedureCode'),
                  values: d.procedureCode ?? undefined,
                  condition: !!showProcedureCode,
                },
                {
                  title: fm('imaging.statementText'),
                  values: d.statement ?? undefined,
                  condition: !!showStatement,
                },
                {
                  title: fm('imaging.hipSubluxationLuxation'),
                  values: d.hipSubluxationLuxation ? fm(`general.${d.hipSubluxationLuxation}`) : undefined,
                },
                {
                  title: fm('imaging.migrationPercentage'),
                  values: exists(d.migrationPercentage) ? `${d.migrationPercentage} %` : undefined,
                  condition: d.hipSubluxationLuxation === 'yes',
                },
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'mri': {
        const events: IEvent[] = [];
        if (platform === 'parkinson') {
          const ariaMedocsForThisMri = ariaInterestedMedications?.filter(
            med =>
              partialDateToValue(med.startDate) < partialDateToValue(d.date) &&
              (!med.endDate || partialDateToValue(d.date) <= partialDateToValue(med.endDate)),
          );
          const mriBeforeMed = preMedMris?.filter(pmm =>
            ariaMedocsForThisMri?.some(arimed => arimed._id === pmm?.medDocId),
          );
          const prevMri =
            (ariaMedocsForThisMri?.length ?? 0) > 0
              ? allMriDocs
                  .toReversed()
                  .find(
                    prev =>
                      partialDateToValue(prev.date) < partialDateToValue(d.date) &&
                      ariaMedocsForThisMri?.some(
                        med => partialDateToValue(med.startDate) < partialDateToValue(prev.date),
                      ),
                  )
              : undefined;
          const otherMrisToDescription = structuredClone(mriBeforeMed) ?? [];
          if (prevMri) {
            otherMrisToDescription.push(prevMri);
          }
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.mri'),
              description: [
                {
                  title: '',
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.medDocId
                        ? fm('graph.beforeMedication', {
                            med:
                              ariaMedocsForThisMri?.find(med => med._id === oMri.medDocId)?.medicationSubstances ?? '',
                          }) + ` (${formatPartialDate(oMri.date)})`
                        : `${fm('imaging.previousMRI')} (${formatPartialDate(oMri?.date)})`,
                    ),
                    formatPartialDate(d.date),
                  ],
                  style: { fontWeight: 600 },
                  condition: otherMrisToDescription.length > 0,
                },
                {
                  title: fm('imaging.procedureCode'),
                  values: [...otherMrisToDescription.map(oMri => oMri?.procedureCode), d.procedureCode],
                  condition: !!(showProcedureCode || otherMrisToDescription.some(other => other?.procedureCode)),
                },
                {
                  title: fm('imaging.statementText'),
                  values: [...otherMrisToDescription.map(oMri => oMri?.statement), d.statement],
                  condition: !!(showStatement || otherMrisToDescription.some(other => other?.statement)),
                },
                {
                  title: fm('imaging.tesla'),
                  values: [...otherMrisToDescription.map(oMri => oMri?.tesla), d.tesla],
                  condition: !!(d.tesla || otherMrisToDescription.some(other => other?.tesla)),
                },
                {
                  title: fm('imaging.ariaE.titleShort'),
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.ariaE?.finding ? fm(`imaging.opts.ariaE.${oMri.ariaE.finding}`) : '-',
                    ),
                    d.ariaE?.finding ? fm(`imaging.opts.ariaE.${d.ariaE.finding}`) : '-',
                  ],
                  condition: !!(d.ariaE?.finding || otherMrisToDescription.some(other => other?.ariaE?.finding)),
                },
                {
                  title: fm('imaging.ariaE.amounts'),
                  values: [
                    ...otherMrisToDescription.map((oMri, i) => (
                      <div key={i}>
                        {Object.entries(oMri?.ariaE?.amounts ?? {})
                          .map(([area, value]) => {
                            if (value || value === 0) {
                              return (
                                <div key={area}>
                                  <span>{fm(`imaging.brainAreas.${area}`) + ': '}</span>
                                  <span>{value}</span>
                                </div>
                              );
                            }
                            return;
                          })
                          .filter(row => row)}
                      </div>
                    )),
                    <div key={'current'}>
                      {Object.entries(d.ariaE?.amounts ?? {})
                        .map(([area, value]) => {
                          if (value || value === 0) {
                            return (
                              <div key={area}>
                                <span>{fm(`imaging.brainAreas.${area}`) + ': '}</span>
                                <span>{value}</span>
                              </div>
                            );
                          }
                          return;
                        })
                        .filter(row => row)}
                    </div>,
                  ],
                  condition:
                    (d.ariaE?.finding && d.ariaE.finding !== 'no') ||
                    otherMrisToDescription.some(other => other?.ariaE?.finding && other.ariaE.finding !== 'no'),
                },
                {
                  title: fm('imaging.ariaH.title'),
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.ariaH?.finding ? fm(`general.${oMri.ariaH.finding}`) : '-',
                    ),
                    d.ariaH?.finding ? fm(`general.${d.ariaH.finding}`) : '-',
                  ],
                  condition: !!(d.ariaH?.finding || otherMrisToDescription.some(other => other?.ariaH?.finding)),
                },
                {
                  title: fm('imaging.ariaH.amountsShort'),
                  values: [
                    ...otherMrisToDescription.map((oMri, i) => (
                      <div key={i}>
                        {Object.entries(oMri?.ariaH?.amounts ?? {})
                          .map(([area, value]) => {
                            if (value || value === 0) {
                              return (
                                <div key={area}>
                                  <span>{fm(`imaging.brainAreas.${area}`) + ': '}</span>
                                  <span>{value}</span>
                                </div>
                              );
                            }
                            return;
                          })
                          .filter(row => row)}
                      </div>
                    )),
                    <div key={'current'}>
                      {Object.entries(d.ariaH?.amounts ?? {})
                        .map(([area, value]) => {
                          if (value || value === 0) {
                            return (
                              <div key={area}>
                                <span>{fm(`imaging.brainAreas.${area}`) + ': '}</span>
                                <span>{value}</span>
                              </div>
                            );
                          }
                          return;
                        })
                        .filter(row => row)}
                    </div>,
                  ],
                  condition:
                    d.ariaH?.finding === 'yes' || otherMrisToDescription.some(other => other?.ariaH?.finding === 'yes'),
                },
                {
                  title: fm('imaging.ariaH.ariaELeak'),
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.ariaH?.ariaELeak ? fm(`general.${oMri.ariaH.ariaELeak}`) : '-',
                    ),
                    d.ariaH?.ariaELeak ? fm(`general.${d.ariaH.ariaELeak}`) : '-',
                  ],
                  condition: !!(
                    (d.ariaH?.finding === 'yes' && d.ariaH.ariaELeak) ||
                    otherMrisToDescription.some(other => other?.ariaH?.finding === 'yes' && other.ariaH.ariaELeak)
                  ),
                },
                {
                  title: fm('imaging.ariaH.ariaHOther'),
                  values: [
                    ...otherMrisToDescription.map(oMri => {
                      return oMri?.ariaH?.ariaHOther ? fm(`general.${oMri.ariaH.ariaHOther}`) : '-';
                    }),
                    d.ariaH?.ariaHOther ? fm(`general.${d.ariaH.ariaHOther}`) : '-',
                  ],
                  condition: !!(d.ariaH?.ariaHOther || otherMrisToDescription.some(other => other?.ariaH?.ariaHOther)),
                },
                {
                  title: fm('imaging.ariaH.diameter'),
                  values: [...otherMrisToDescription.map(oMri => oMri?.ariaH?.diameter), d.ariaH?.diameter],
                  condition: !!(
                    (d.ariaH?.ariaHOther === 'yes' && isNumber(d.ariaH.diameter)) ||
                    otherMrisToDescription.some(other => isNumber(other?.ariaH?.diameter))
                  ),
                },
                {
                  title: fm('imaging.ariaNotes'),
                  values: [...otherMrisToDescription.map(oMri => oMri?.ariaNotes), d.ariaNotes],
                  condition: !!(d.ariaNotes || otherMrisToDescription.some(other => other?.ariaNotes)),
                },
                {
                  title: fm('imaging.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous'),
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous
                        ? fm(
                            `imaging.opts.yesFazekas0.${oMri.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous}`,
                          )
                        : '-',
                    ),
                    d.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous
                      ? fm(`imaging.opts.yesFazekas0.${d.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous}`)
                      : undefined,
                  ],
                  condition: !!(
                    d.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous ||
                    otherMrisToDescription.some(
                      other => other?.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous,
                    )
                  ),
                },
                {
                  title: fm('imaging.wasFazekasClassificationDone'),
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.wasFazekasClassificationDone
                        ? fm(`imaging.opts.yesNo.${oMri.wasFazekasClassificationDone}`)
                        : '-',
                    ),
                    d.wasFazekasClassificationDone
                      ? fm(`imaging.opts.yesNo.${d.wasFazekasClassificationDone}`)
                      : undefined,
                  ],
                  condition: !!(
                    (d.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous === 'yes' &&
                      d.wasFazekasClassificationDone) ||
                    otherMrisToDescription.some(
                      other =>
                        other?.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous === 'yes' &&
                        other.wasFazekasClassificationDone,
                    )
                  ),
                },
                {
                  title: fm('imaging.fazekasGrade'),
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.fazekasGrade ? fm(`imaging.opts.fazekas.${oMri.fazekasGrade}`) : '-',
                    ),
                    d.fazekasGrade ? fm(`imaging.opts.fazekas.${d.fazekasGrade}`) : undefined,
                  ],
                  condition: !!(
                    (d.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous === 'yes' &&
                      d.wasFazekasClassificationDone === 'yes' &&
                      d.fazekasGrade) ||
                    otherMrisToDescription.some(
                      other =>
                        other?.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous === 'yes' &&
                        other.wasFazekasClassificationDone === 'yes' &&
                        other.fazekasGrade,
                    )
                  ),
                },
                {
                  title: fm('imaging.atrophy'),
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.atrophy ? fm(`imaging.opts.atrophy.${oMri.atrophy}`) : '-',
                    ),
                    d.atrophy ? fm(`imaging.opts.atrophy.${d.atrophy}`) : undefined,
                  ],
                  condition: !!(d.atrophy || otherMrisToDescription.some(other => other?.atrophy)),
                },
                {
                  title: fm('imaging.otherClinicallyRelevantFindingFound'),
                  values: [
                    ...otherMrisToDescription.map(oMri =>
                      oMri?.otherClinicallyRelevantFindingFound
                        ? fm(`imaging.opts.yesNo.${oMri.otherClinicallyRelevantFindingFound}`)
                        : '-',
                    ),
                    d.otherClinicallyRelevantFindingFound
                      ? fm(`imaging.opts.yesNo.${d.otherClinicallyRelevantFindingFound}`)
                      : undefined,
                  ],
                  condition: !!(
                    d.otherClinicallyRelevantFindingFound ||
                    otherMrisToDescription.some(other => other?.otherClinicallyRelevantFindingFound)
                  ),
                },
                {
                  title: fm('imaging.whatFinding'),
                  values: [
                    ...otherMrisToDescription.map(oMri => oMri?.otherClinicallyRelevantFinding),
                    d.otherClinicallyRelevantFinding,
                  ],
                  condition: !!(
                    (d.otherClinicallyRelevantFindingFound === 'yes' && d.otherClinicallyRelevantFinding) ||
                    otherMrisToDescription.some(
                      other =>
                        other?.otherClinicallyRelevantFindingFound === 'yes' && other.otherClinicallyRelevantFinding,
                    )
                  ),
                },
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        } else if (platform === 'ms') {
          let lesionsKey: 'lesions' | 'lesionsDelta';
          let lesionsTitle = fm('imaging.lesions');
          if (index === array.length - 1) {
            lesionsKey = 'lesions';
          } else {
            lesionsKey = 'lesionsDelta';
            lesionsTitle += ' Δ';
          }
          const hideLesionsTrend = lesionsKey === 'lesions';
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              priority: 'low',
              title: fm('imaging.mri'),
              description: [
                {
                  title: fm('imaging.procedureCode'),
                  values: d.procedureCode,
                  condition: !!showProcedureCode,
                },
                {
                  title: fm('imaging.statementText'),
                  values: d.statement,
                  condition: !!showStatement,
                },
                { values: hideLesionsTrend ? [lesionsTitle] : [lesionsTitle, fm('imaging.trend')] },
                {
                  title: fm('imaging.brainT2FlairTotalShort'),
                  values: [
                    d.brainT2Flair?.total?.[lesionsKey],
                    convertTrendToIcon(d.brainT2Flair?.total?.lesionsTrend) ?? '-',
                  ].slice(0, hideLesionsTrend ? 1 : undefined),
                },
                {
                  title: fm('imaging.brainT2FlairInfraShort'),
                  values: [
                    d.brainT2Flair?.infratentorial?.[lesionsKey],
                    convertTrendToIcon(d.brainT2Flair?.infratentorial?.lesionsTrend) ?? '-',
                  ].slice(0, hideLesionsTrend ? 1 : undefined),
                },
                {
                  title: fm('imaging.brainT2FlairSupraShort'),
                  values: [
                    d.brainT2Flair?.supratentorial?.[lesionsKey],
                    convertTrendToIcon(d.brainT2Flair?.supratentorial?.lesionsTrend) ?? '-',
                  ].slice(0, hideLesionsTrend ? 1 : undefined),
                },
                {
                  title: fm('imaging.brainT1GadoliniumShort'),
                  values: [
                    d.brainT1?.gadoliniumEnhanced?.[lesionsKey],
                    convertTrendToIcon(d.brainT1?.gadoliniumEnhanced?.lesionsTrend) ?? '-',
                  ].slice(0, hideLesionsTrend ? 1 : undefined),
                },
                {
                  title: fm('imaging.spinalCordT2TotalShort'),
                  values: [
                    d.spinalCordT2?.total?.[lesionsKey],
                    convertTrendToIcon(d.spinalCordT2?.total?.lesionsTrend) ?? '-',
                  ].slice(0, hideLesionsTrend ? 1 : undefined),
                },
                {
                  title: fm('imaging.spinalCordT1GadoliniumShort'),
                  values: [
                    d.spinalCordT1?.gadoliniumEnhanced?.[lesionsKey],
                    convertTrendToIcon(d.spinalCordT1?.gadoliniumEnhanced?.lesionsTrend) ?? '-',
                  ].slice(0, hideLesionsTrend ? 1 : undefined),
                },
              ],
            });

          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        } else if (platform === 'sma' || platform === 'ninmt') {
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.mri'),
              description: [
                {
                  title: fm('imaging.procedureCode'),
                  values: d.procedureCode ?? undefined,
                  condition: !!showProcedureCode,
                },
                {
                  title: fm('imaging.statementText'),
                  values: d.statement ?? undefined,
                  condition: !!showStatement,
                },
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'scintigraphy': {
        if (platform === 'parkinson') {
          const events: IEvent[] = [];
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.scintigraphy'),
              description: [
                {
                  title: fm('imaging.procedureCode'),
                  values: d.procedureCode ?? undefined,
                  condition: !!showProcedureCode,
                },
                {
                  title: fm('imaging.statementText'),
                  values: d.statement ?? undefined,
                  condition: !!showStatement,
                },
                {
                  title: fm('imaging.imagingType'),
                  values: d.imagingType ? fm(`imaging.opts.imagingType.${d.imagingType}`) : '-',
                  condition: !!d.imagingType,
                },
                {
                  title: fm('imaging.tracer'),
                  values: d.tracer ? fm(`imaging.opts.tracer.${d.tracer}`) : '-',
                  condition: !!(d.imagingType === 'pet' && d.tracer),
                },
                {
                  title: fm('imaging.whatTracer'),
                  values: d.otherTracer ?? undefined,
                  condition: !!(d.imagingType === 'pet' && d.tracer === 'other' && d.otherTracer),
                },
                {
                  title: fm('imaging.amyloidTracer'),
                  values: d.amyloidTracer ? fm(`imaging.opts.amyloidTracer.${d.amyloidTracer}`) : '-',
                  condition: !!(d.imagingType === 'pet' && d.tracer === 'amyloid' && d.amyloidTracer),
                },
                {
                  title: fm('imaging.whatTracer'),
                  values: d.otherTracer ?? undefined,
                  condition: !!(
                    d.imagingType === 'pet' &&
                    d.tracer === 'amyloid' &&
                    d.amyloidTracer === 'other' &&
                    d.otherTracer
                  ),
                },
                {
                  title: fm('imaging.result'),
                  values: d.result ? fm(`imaging.opts.result.${d.result}`) : '-',
                  condition: !!d.result,
                },
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'tt': {
        if (platform === 'parkinson') {
          const events: IEvent[] = [];
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm('imaging.tt'),
              description: [
                {
                  title: fm('imaging.procedureCode'),
                  values: d.procedureCode ?? undefined,
                  condition: !!showProcedureCode,
                },
                {
                  title: fm('imaging.statementText'),
                  values: d.statement ?? undefined,
                  condition: !!showStatement,
                },
                {
                  title: fm('imaging.atrophy'),
                  values: d.atrophy ? fm(`imaging.opts.atrophy.${d.atrophy}`) : '-',
                  condition: !!d.atrophy,
                },
                {
                  title: fm('imaging.atrophyLocalization'),
                  values: d.atrophyLocalization ?? undefined,
                  condition: !!(d.atrophy === 'focal' && d.atrophyLocalization),
                },
                {
                  title: fm('imaging.otherClinicalRelevant'),
                  values: d.otherClinicallyRelevantFindingFound
                    ? fm(`general.${d.otherClinicallyRelevantFindingFound}`)
                    : '-',
                  condition: !!d.otherClinicallyRelevantFindingFound,
                },
                {
                  title: fm('imaging.otherClinicalRelevant'),
                  values: d.otherClinicallyRelevantFinding ?? undefined,
                  condition: !!(d.otherClinicallyRelevantFindingFound === 'yes' && d.otherClinicallyRelevantFinding),
                },
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'thymusCt':
      case 'thymusMri': {
        if (platform === 'mgravis') {
          const events: IEvent[] = [];
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'study',
              title: fm(`imaging.${d._type}`),
              description: [
                {
                  title: fm('imaging.finding'),
                  values:
                    Array.isArray(d.finding) && d.finding.length > 0
                      ? d.finding
                          .map((f, i, arr) => `${fm(`imaging.opts.finding.${f}`)}${i < arr.length - 1 ? ', ' : ''}`)
                          .join('')
                      : d.finding
                        ? fm(`imaging.opts.finding.${d.finding}`)
                        : '-',
                },
              ],
            });
          addons.push({
            id: 'imaging',
            title: fm('imaging.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
    }
  });
  if (platform === 'parkinson' && ariaSymptomDocs && ariaSymptomDocs.length > 0) {
    const events: IEvent[] = [];
    ariaSymptomDocs.forEach(as => {
      as.date &&
        events.push({
          date: dateFromPartialUpdateTimeframe(as.date),
          eventType: 'treatment',
          title: fm('ariaSymptoms.title'),
          description: [
            {
              title: fm('ariaSymptoms.symptomsFound'),
              values: as.symptomsFound ? fm(`general.${as.symptomsFound}`) : '-',
            },
            {
              title: fm('ariaSymptoms.symptoms'),
              values: (
                <div>
                  {as.symptoms?.map(symp => {
                    return <div key={symp}>{fm(`ariaSymptoms.opts.${symp}`)}</div>;
                  })}
                </div>
              ),
              condition: as.symptomsFound === 'yes',
            },
            {
              title: fm('ariaSymptoms.additionalInformation'),
              values: as.additionalInformation ?? undefined,
              condition: !!as.additionalInformation,
            },
          ],
        });
    });
    addons.push({
      id: 'imaging',
      title: fm('imaging.title'),
      titleDescription: undefined,
      items: [],
      events,
    });
  }

  return addons.length > 0 ? addons : undefined;
};

export const convertClinicalStudyToTimeline = (
  docs: Array<
    IDocumentWithTypeV2<
      | 'enmg'
      | 'nocturnalPolygraphy'
      | 'fvc'
      | 'vep'
      | 'sep'
      | 'baep'
      | 'octscan'
      | 'ecg'
      | 'echocardiography'
      | 'bloodPressure'
      | 'heartRate'
    >
  >,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  platform: Platform | null,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(d => {
    switch (d._type) {
      case 'enmg': {
        if (platform && ['sma', 'dmd'].includes(platform)) {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.enmg'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'nocturnalPolygraphy': {
        if (platform && ['sma', 'dmd'].includes(platform)) {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.nocturnalPolygraphy'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'fvc': {
        if (platform && ['sma', 'dmd'].includes(platform)) {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.fvc'),
            description: [
              { title: fm('clinicalstudy.fvcLitre'), values: isNumber(d.litres) ? d.litres : undefined },
              {
                title: fm('clinicalstudy.fvcPredictedPercentage'),
                values: isNumber(d.predicted) ? d.predicted : undefined,
              },
              {
                title: fm('clinicalstudy.sitOrLieDown'),
                values: d.sittingOrLyingDown ? fm(`clinicalstudy.opts.${d.sittingOrLyingDown}`) : undefined,
              },
            ],
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'vep': {
        if (platform === 'ms') {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.vep'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'sep': {
        if (platform === 'ms') {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.sep'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'baep': {
        if (platform === 'ms') {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.baep'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'octscan': {
        if (platform === 'ms') {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.octscan'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'ecg': {
        if (platform === 'ms') {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.ecg'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'echocardiography': {
        if (platform === 'ms') {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.echocardiography'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'bloodPressure': {
        if (platform === 'ms') {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.bloodPressure'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
      case 'heartRate': {
        if (platform === 'ms') {
          if (!isPartialDate(d.date)) return undefined;
          const events: IEvent[] = [];
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'study',
            title: fm('clinicalstudy.heartRate'),
          });
          addons.push({
            id: 'clinicalStudy',
            title: fm('clinicalstudy.title'),
            titleDescription: undefined,
            items: [],
            events,
          });
        }
        break;
      }
    }

    return;
  });
  if (addons.length > 0) {
    const allEvents: Array<IEvent> = [];
    addons.forEach(addon => addon.events?.forEach(event => allEvents.push(event)));
    const addonsMerged = { ...addons[0], events: allEvents } as IAddon;
    return [addonsMerged];
  }
  return undefined;
};

export const convertExitusToTimeline = (
  exitusdata: TExitusUI,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  ageMonths: number,
  fm: (id: string) => string,
): IAddon | undefined => {
  const events: IEvent[] = [];
  const deadAtAge = ageMonths < 60 ? Math.floor(ageMonths) : Math.floor(ageMonths / 12);
  if (exitusdata.dateOfDeath) {
    events.push({
      date: dateFromPartialUpdateTimeframe(exitusdata.dateOfDeath),
      eventType: 'text',
      title: 'EXITUS',
      description: [
        { title: `${formatPartialDate(exitusdata.dateOfDeath)}` },
        { title: `${fm(`graph.${ageMonths < 60 ? 'ageOfDeathMonths' : 'ageOfDeath'}`)}: ${deadAtAge}` },
      ],
    });
  }
  return events.length > 0 ? { title: '', id: 'topdata', events } : undefined;
};

const keyDiagnoses: string[] = ['M41', 'M41.4', 'M40', 'M40.0', 'M40.1', 'M40.2', 'Q65', 'M24.5'];

export const convertComorbidityToTimeline = (
  docs: IDocumentWithTypeV2<'comorbidity'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  uiLang: Locale,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(d => {
    // If a new key diagnosis needocs to be added
    if (d.classification?.icd10code && new RegExp(keyDiagnoses.join('|')).test(d.classification.icd10code)) {
      const events: IEvent[] = [];
      d.date &&
        events.push({
          date: dateFromPartialUpdateTimeframe(d.date),
          eventType: 'text',
          title: d.classification.icd10code,
          description: getICD10Description(d.classification.icd10code, uiLang).then(
            v => `${d.classification?.icd10code} - ${v}`,
          ),
        });
      addons.push({
        id: 'keyDiagnoses',
        title: fm('comorbidity.keyDiagnoses.title'),
        items: [],
        events,
      });
    }

    // If new corset treatments need to be added
    if (d.corsetTreatments && d.corsetTreatments.length > 0) {
      const items: IItem[] = [];
      d.corsetTreatments.forEach(ct => {
        ct.startDate &&
          items.push({
            start: dateFromPartialUpdateTimeframe(ct.startDate),
            end: ct.endDate ? dateFromPartialUpdateTimeframe(ct.endDate) : undefined,
            title: fm('comorbidity.corsetTreatment'),
            description: [
              {
                title: fm('comorbidity.corsetType'),
                values: ct.corsetType ? fm(`comorbidity.opts.corsetType.${ct.corsetType}`) : undefined,
              },
            ],
          });
      });
      addons.push({
        id: 'corsetTreatment',
        title: fm('comorbidity.corsetTreatment'),
        titleDescription: undefined,
        items,
        events: [],
      });
    }

    // If new back surgeries need to be added
    if (d.backSurgeries && d.backSurgeries.length > 0) {
      const events: IEvent[] = [];
      d.backSurgeries.forEach(bs => {
        bs.date &&
          events.push({
            date: dateFromPartialUpdateTimeframe(bs.date),
            eventType: 'treatment',
            title: fm('comorbidity.backSurgery'),
            description: [
              {
                title: fm('comorbidity.surgeryTechnique'),
                values: bs.surgeryTechnique
                  ? fm(`comorbidity.opts.surgeryTechnique.${bs.surgeryTechnique}`)
                  : undefined,
              },
              {
                title: fm('comorbidity.instrumentation'),
                values: bs.instrumentation ? fm(`comorbidity.opts.instrumentation.${bs.instrumentation}`) : undefined,
              },
              {
                title: fm('comorbidity.additionalInformation'),
                values: bs.instrumentationAdditionalInformation ?? undefined,
                condition: bs.instrumentation === 'other',
              },
              { title: fm('comorbidity.surgeryLevel'), values: bs.surgeryLevel ?? undefined },
            ],
          });
      });
      addons.push({
        id: 'backSurgery',
        title: fm('comorbidity.backSurgery'),
        titleDescription: undefined,
        items: [],
        events,
      });
    }

    // If new hip surgeries need to be added
    if (d.hipSurgeries && d.hipSurgeries.length > 0) {
      const events: IEvent[] = [];
      d.hipSurgeries.forEach(hs => {
        hs.date &&
          events.push({
            date: dateFromPartialUpdateTimeframe(hs.date),
            eventType: 'treatment',
            title: fm('comorbidity.hipSurgery'),
            description: [
              {
                title: fm('comorbidity.surgeryType'),
                values: hs.surgeryType ? fm(`comorbidity.opts.surgeryType.${hs.surgeryType}`) : undefined,
              },
            ],
          });
      });
      addons.push({
        id: 'hipSurgery',
        title: fm('comorbidity.hipSurgery'),
        titleDescription: undefined,
        items: [],
        events,
      });
    }

    return;
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertSdmtToGraph = (
  docs: IDocumentWithTypeV2<'sdmt'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string, values?: { [key: string]: string }) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const value = d.correctAnswers || d.correctAnswers === 0 ? Number(d.correctAnswers) : undefined;
    if ((value || value === 0) && !Number.isNaN(value)) {
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value,
        id: 'sdmt',
        title: value.toString(),
        description: [
          {
            title: fm('cognition.sdmt.correctAnswers'),
            values: <span style={{ fontWeight: 600 }}>{d.correctAnswers ?? '-'}</span>,
            style: { minWidth: '20rem' },
          },
          {
            title: fm('cognition.sdmt.allAnswers'),
            values: isNumber(d.allAnswers) ? d.allAnswers : undefined,
          },
          {
            title: fm('cognition.sdmt.testType'),
            values: d.testType ? fm(`cognition.sdmt.${d.testType}`) : undefined,
          },
        ],
        hideValue: true,
      });
    }
  });
  return [
    {
      dataPoints,
      type: 'lineGraph',
      legend: 'SDMT',
      id: 'sdmt',
    },
  ];
};

export const convertMeasurementsToGraph = (
  documents: IDocumentV2[],
  myDocuments: IDocumentV2[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string, values?: { [key: string]: string }) => string,
  type: 'weights' | 'heights' | 'bmi' | 'ybocs',
  patientDob: PatientDemographics['dateOfBirth'], // Required for BMI logic
): Array<IData> | undefined => {
  const dataPoints: IDataPoint[] = [];

  const docs = documents.map(mapDocumentToNeuroDocument);
  const myDocs = myDocuments.map(mapDocumentToNeuroDocument);

  if (type === 'bmi') {
    const bmiData = getBMITimeseries(docs, myDocs, patientDob);
    const series = bmiData.series['bmi'] ?? [];

    // Graph expects ascending order (oldest --> newest)
    const sortedForGraph = series.toSorted((a, b) => sortPartialDate(a.date, b.date));

    sortedForGraph.forEach(pt => {
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(pt.date),
        value: pt.value,
        id: 'bmi',
        title: fm('background.bmiUnit', { value: String(pt.value) }),
        description: pt.isCalculated ? fm('background.bmiCalculated') : undefined,
      });
    });
  } else {
    // Standard extraction
    const mData = getMeasurementTimeseries(docs);
    const bgData = getBackgroundTimeseries(docs, myDocs);

    // Helper to merge measurement + background and sort ascending
    const getMergedSeries = (key: string) => {
      return [...(mData.series[key] ?? []), ...(bgData.series[key] ?? [])].toSorted((a, b) =>
        sortPartialDate(a.date, b.date),
      );
    };

    const safeNum = (v: unknown) => (typeof v === 'number' ? v : parseFloat(String(v)) || 0);

    if (type === 'weights') {
      // NOTE: Original code only showed 'docs' (measurements) for weights, not background.
      // To strictly match "HCP only" for weights as per previous logic:
      const series = mData.series['weight'] ?? [];
      series.sort((a, b) => sortPartialDate(a.date, b.date)); // ascending

      series.forEach(pt => {
        dataPoints.push({
          date: dateFromPartialUpdateTimeframe(pt.date),
          value: safeNum(pt.value),
          id: 'weight',
          title: fm('background.kg', { value: String(pt.value) }),
        });
      });
    } else if (type === 'heights') {
      getMergedSeries('height').forEach(pt => {
        dataPoints.push({
          date: dateFromPartialUpdateTimeframe(pt.date),
          value: safeNum(pt.value),
          id: 'height',
          title: fm('background.cm', { value: String(pt.value) }),
        });
      });
    } else {
      const main = getMergedSeries('ybocs');
      const pa = getMergedSeries('ybopa');
      const pt = getMergedSeries('ybopt');

      main.forEach(item => {
        const dKey = item.date.join('-');
        const paVal = pa.find(x => x.date.join('-') === dKey)?.value;
        const ptVal = pt.find(x => x.date.join('-') === dKey)?.value;

        dataPoints.push({
          date: dateFromPartialUpdateTimeframe(item.date),
          value: safeNum(item.value),
          id: 'ybocs',
          title: String(item.value),
          description: (
            <div>
              {paVal && <div>{fm('graph.ybopa') + ': ' + paVal}</div>}
              {ptVal && <div>{fm('graph.ybopt') + ': ' + ptVal}</div>}
            </div>
          ),
        });
      });
    }
  }

  if (dataPoints.length === 0) return undefined;

  let legend = '';
  switch (type) {
    case 'weights':
      legend = fm('background.weight');
      break;
    case 'heights':
      legend = fm('background.height');
      break;
    case 'bmi':
      legend = fm('background.bmi');
      break;
    case 'ybocs':
      legend = fm('graph.ybocsWithScale');
      break;
  }

  return [
    {
      dataPoints,
      id: type,
      type: 'lineGraph',
      legend,
    },
  ];
};

export const convertPregnancyToTimeline = (
  docs: Array<IDocumentWithTypeV2<'mgravisBackground' | 'background'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];

  const pregnancies = docs.find(d => d.pregnancies && d.pregnancies.length > 0)?.pregnancies;

  pregnancies &&
    pregnancies.forEach(preg => {
      if (!isPartialDate(preg.startDate)) return;
      const items: IItem[] = [];
      items.push({
        start: dateFromPartialUpdateTimeframe(preg.startDate),
        end: isPartialDate(preg.endDate) ? dateFromPartialUpdateTimeframe(preg.endDate) : undefined,
        title: fm('graph.pregnancy'),
      });
      addons.push({
        id: 'pregnancy',
        title: fm('graph.pregnancy'),
        titleDescription: undefined,
        items,
        events: [],
      });
    });
  return addons.length > 0 ? addons : undefined;
};

export const convertExaminationsToGraph = (
  docs: Array<IDocumentWithTypeV2<'examination'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string, values?: { [key: string]: string }) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];

  const codename = docs.find(d => d.codeName)?.codeName;

  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;

    const examinationCodeName = d.codeName;
    const examinationValue = d.value && typeof d.value === 'string' ? parseFloat(d.value) : d.value;
    const isAbnormal = d.abnormalFlags && abnormalAbnormalFlags.includes(d.abnormalFlags);

    if (examinationValue && examinationCodeName) {
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: examinationValue,
        id: 'examination' + examinationCodeName,
        title: `${examinationValue} ${d.units}`,
        description: [
          {
            title: fm('examination.valueReferenceRange'),
            values: <span style={{ fontWeight: 600 }}>{d.valueReferenceRange ?? '-'}</span>,
            style: { minWidth: '20rem' },
          },
          {
            title: fm('examination.abnormalFlags'),
            values: (
              <span style={{ fontWeight: 600 }}>
                {d.abnormalFlags
                  ? d.abnormalFlags +
                    `${
                      abnormalFlagsWithDescription.includes(d.abnormalFlags || '')
                        ? ' - ' + fm('examination.abnormalFlagsDescription.' + d.abnormalFlags)
                        : ''
                    }`
                  : '-'}
              </span>
            ),
            style: { minWidth: '20rem' },
          },
          {
            title: fm('examination.comments'),
            values: <span style={{ fontWeight: 600 }}>{d.comments ?? '-'}</span>,
            style: { minWidth: '20rem' },
          },
        ],
        pointType: isAbnormal ? 'triangle' : 'circle',
      });
    }
  });

  return [
    {
      dataPoints,
      id: 'examination',
      type: 'lineGraph',
      legend: codename ?? '',
    },
  ];
};
